import {
  Injectable,
  PLATFORM_ID,
  inject,
  signal,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { GOOGLE_MAPS_JS_API_LOADER } from './provide-google-maps-js-api-loader';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';

type MapLoadingServiceState = {
  status: 'unloaded' | 'loading' | 'failed' | 'loaded';
  mapsLibrary: google.maps.MapsLibrary | null;
};

@Injectable({
  providedIn: 'root',
})
export class MapLoadingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly gmjal = inject(GOOGLE_MAPS_JS_API_LOADER);
  private readonly injector = inject(Injector);

  private state = signal<MapLoadingServiceState>({
    mapsLibrary: null,
    status: 'unloaded',
  });

  /**
   * Observable that emits when the Google Maps library is loaded.
   * This has a side-effect of loading the library if it is not already loaded.
   */
  isMapLoaded$ = toObservable(this.state).pipe(
    tap(() =>
      runInInjectionContext(this.injector, () => {
        const isUninitialized = this.state().status === 'unloaded';
        if (isUninitialized && isPlatformBrowser(this.platformId)) {
          this.loadGoogleMapsLibrary();
        }
      }),
    ),
    map((state) => state.mapsLibrary !== null),
    catchError(() => of(false)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  isMapLoaded = toSignal(this.isMapLoaded$, { initialValue: false });

  async loadGoogleMapsLibrary(): Promise<google.maps.MapsLibrary | null> {
    return runInInjectionContext(this.injector, async () => {
      if (!isPlatformBrowser(this.platformId)) {
        // Google Maps only work in the browser.
        return null;
      }
      const state = this.state();
      if (state.status !== 'unloaded' || state.mapsLibrary !== null) {
        return state.mapsLibrary;
      }
      this.state.update((s) => ({ ...s, status: 'loading' }));

      let library: google.maps.MapsLibrary | null;
      try {
        library = await this.gmjal.importLibrary('maps');
      } catch {
        library = null;
        this.state.set({ status: 'failed', mapsLibrary: null });
        return null;
      }
      this.state.set({ status: 'loaded', mapsLibrary: library });
      return library;
    });
  }
}
